import { createService, deleteService, updateService } from "@repo/ui";
import { getFirestore } from "firebase/firestore";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import type { Service } from "../types";
import { TypedDeleteConfirmationDialog } from "./TypedDeleteConfirmationDialog";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";
import { Textarea } from "./ui/textarea";

interface ServicesPageProps {
	onNavigateToService: (serviceId: string) => void;
	createForCompanyId?: string | null;
	onCreateDialogClose?: () => void;
}

export function ServicesPage({
	onNavigateToService,
	createForCompanyId,
	onCreateDialogClose,
}: ServicesPageProps) {
	const { services, companies } = useData();
	const db = getFirestore();
	const [searchTerm, setSearchTerm] = useState("");
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(
		Boolean(createForCompanyId),
	);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [selectedService, setSelectedService] = useState<Service | null>(null);
	const [formData, setFormData] = useState({
		companyId: createForCompanyId ?? "",
		name: "",
		description: "",
		website: "",
		serviceBrief: "",
		contactDetails: "",
		faq: "",
		knowledgeBase: "",
	});

	const servicesWithCompany = useMemo(() => {
		return services.map((service) => ({
			...service,
			companyName:
				companies.find((c) => c.id === service.companyId)?.name || "Unknown",
		}));
	}, [services, companies]);

	const filteredServices = useMemo(() => {
		if (!searchTerm) return servicesWithCompany;

		return servicesWithCompany.filter(
			(service) =>
				service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				service.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
				service.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
				(service.serviceBrief || "")
					.toLowerCase()
					.includes(searchTerm.toLowerCase()) ||
				(service.faq || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
				(service.knowledgeBase || "")
					.toLowerCase()
					.includes(searchTerm.toLowerCase()),
		);
	}, [servicesWithCompany, searchTerm]);

	const handleAddSubmit = async () => {
		// Validate form
		if (!(createForCompanyId ?? formData.companyId) || !formData.name) {
			toast.error("Please fill in required fields");
			return;
		}

		const companyName =
			companies.find((c) => c.id === (createForCompanyId ?? formData.companyId))
				?.name || "Unknown";

		try {
			await createService(db, {
				...formData,
				companyId: createForCompanyId ?? formData.companyId,
				additionalResources: [],
			});
			toast.success(
				`Service "${formData.name}" created successfully for ${companyName}`,
			);

			// Reset form and close dialog
			setFormData({
				companyId: "",
				name: "",
				description: "",
				website: "",
				serviceBrief: "",
				contactDetails: "",
				faq: "",
				knowledgeBase: "",
			});
			setIsAddDialogOpen(false);
			onCreateDialogClose?.();
		} catch (error) {
			console.error("Failed to create service", error);
			toast.error("Failed to create service");
		}
	};

	const handleEditClick = (service: Service) => {
		setSelectedService(service);
		setFormData({
			companyId: service.companyId ?? "",
			name: service.name,
			description: service.description,
			website: service.website ?? "",
			serviceBrief: service.serviceBrief || "",
			contactDetails: service.contactDetails || "",
			faq: service.faq || "",
			knowledgeBase: service.knowledgeBase || "",
		});
		setIsEditDialogOpen(true);
	};

	const handleEditSubmit = async () => {
		if (!selectedService) return;

		// Validate form
		if (!formData.companyId || !formData.name) {
			toast.error("Please fill in required fields");
			return;
		}

		try {
			await updateService(
				db,
				selectedService.companyId,
				selectedService.id,
				formData,
			);
			toast.success(`Service "${formData.name}" updated successfully`);
			setIsEditDialogOpen(false);
			setSelectedService(null);
		} catch (error) {
			console.error("Failed to update service", error);
			toast.error("Failed to update service");
		}
	};

	const handleDeleteClick = (service: Service) => {
		setSelectedService(service);
		setIsDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!selectedService) return;

		try {
			await deleteService(db, selectedService.companyId, selectedService.id);
			toast.success(`Service "${selectedService.name}" deleted successfully`);
			setIsDeleteDialogOpen(false);
			setSelectedService(null);
		} catch (error) {
			console.error("Failed to delete service", error);
			toast.error("Failed to delete service");
		}
	};

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-start">
				<div>
					<h1>Services</h1>
					<p className="text-slate-600">
						Manage AI agent services across all companies
					</p>
				</div>
				<Dialog
					open={isAddDialogOpen}
					onOpenChange={(open) => {
						setIsAddDialogOpen(open);
						if (!open) onCreateDialogClose?.();
					}}
				>
					<DialogTrigger asChild>
						<Button>
							<Plus className="mr-2 h-4 w-4" />
							Add Service
						</Button>
					</DialogTrigger>
					<DialogContent className="admin-editor-dialog admin-editor-dialog--wide">
						<DialogHeader>
							<DialogTitle>Add New Service</DialogTitle>
							<DialogDescription>
								Create a new AI agent service. Required fields are marked with
								*.
							</DialogDescription>
						</DialogHeader>
						<div className="admin-editor-body admin-editor-grid admin-service-editor-body">
							<div className="space-y-2">
								<Label htmlFor="service-company">
									Company <span className="text-red-500">*</span>
								</Label>
								<Select
									disabled={Boolean(createForCompanyId)}
									value={formData.companyId}
									onValueChange={(value: string) =>
										setFormData({ ...formData, companyId: value })
									}
								>
									<SelectTrigger id="service-company">
										<SelectValue placeholder="Select a company" />
									</SelectTrigger>
									<SelectContent>
										{companies.map((company) => (
											<SelectItem key={company.id} value={company.id}>
												{company.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label htmlFor="service-name">
									Service Name <span className="text-red-500">*</span>
								</Label>
								<Input
									id="service-name"
									placeholder="Customer Support Line"
									value={formData.name}
									onChange={(e) =>
										setFormData({ ...formData, name: e.target.value })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="service-description">Description</Label>
								<Textarea
									id="service-description"
									placeholder="Main customer support hotline"
									value={formData.description}
									onChange={(e) =>
										setFormData({ ...formData, description: e.target.value })
									}
									rows={3}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="service-website">Website</Label>
								<Input
									id="service-website"
									type="url"
									placeholder="https://support.example.com"
									value={formData.website}
									onChange={(e) =>
										setFormData({ ...formData, website: e.target.value })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="service-brief">Service Brief</Label>
								<Textarea
									id="service-brief"
									placeholder="Concise overview of the business service"
									value={formData.serviceBrief}
									onChange={(e) =>
										setFormData({
											...formData,
											serviceBrief: e.target.value,
										})
									}
									rows={6}
									className="min-h-32 break-words whitespace-pre-wrap"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="service-contact">Contact Details</Label>
								<Textarea
									id="service-contact"
									placeholder="Phone, email, hours, escalation paths"
									value={formData.contactDetails}
									onChange={(e) =>
										setFormData({ ...formData, contactDetails: e.target.value })
									}
									rows={3}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="service-faq">FAQ</Label>
								<Textarea
									id="service-faq"
									placeholder="Frequently asked questions and preferred answers"
									value={formData.faq}
									onChange={(e) =>
										setFormData({ ...formData, faq: e.target.value })
									}
									rows={5}
									className="max-h-56 overflow-y-auto"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="service-knowledge">Knowledge Base</Label>
								<Textarea
									id="service-knowledge"
									placeholder="Detailed service knowledge, edge cases, policies"
									value={formData.knowledgeBase}
									onChange={(e) =>
										setFormData({ ...formData, knowledgeBase: e.target.value })
									}
									rows={7}
									className="max-h-72 overflow-y-auto"
								/>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setIsAddDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button onClick={handleAddSubmit}>Create Service</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Edit Dialog */}
			<Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
				<DialogContent className="admin-editor-dialog admin-editor-dialog--wide">
					<DialogHeader>
						<DialogTitle>Edit Service</DialogTitle>
						<DialogDescription>
							Update service details. Required fields are marked with *.
						</DialogDescription>
					</DialogHeader>
					<div className="admin-editor-body admin-editor-grid admin-service-editor-body">
						<div className="space-y-2">
							<Label htmlFor="edit-service-company">
								Company <span className="text-red-500">*</span>
							</Label>
							<Select
								value={formData.companyId}
								onValueChange={(value: string) =>
									setFormData({ ...formData, companyId: value })
								}
							>
								<SelectTrigger id="edit-service-company">
									<SelectValue placeholder="Select a company" />
								</SelectTrigger>
								<SelectContent>
									{companies.map((company) => (
										<SelectItem key={company.id} value={company.id}>
											{company.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-service-name">
								Service Name <span className="text-red-500">*</span>
							</Label>
							<Input
								id="edit-service-name"
								placeholder="Customer Support Line"
								value={formData.name}
								onChange={(e) =>
									setFormData({ ...formData, name: e.target.value })
								}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-service-description">Description</Label>
							<Textarea
								id="edit-service-description"
								placeholder="Main customer support hotline"
								value={formData.description}
								onChange={(e) =>
									setFormData({ ...formData, description: e.target.value })
								}
								rows={3}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-service-website">Website</Label>
							<Input
								id="edit-service-website"
								type="url"
								placeholder="https://support.example.com"
								value={formData.website}
								onChange={(e) =>
									setFormData({ ...formData, website: e.target.value })
								}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-service-brief">Service Brief</Label>
							<Textarea
								id="edit-service-brief"
								placeholder="Concise overview of the business service"
								value={formData.serviceBrief}
								onChange={(e) =>
									setFormData({ ...formData, serviceBrief: e.target.value })
								}
								rows={6}
								className="min-h-32 break-words whitespace-pre-wrap"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-service-contact">Contact Details</Label>
							<Textarea
								id="edit-service-contact"
								placeholder="Phone, email, hours, escalation paths"
								value={formData.contactDetails}
								onChange={(e) =>
									setFormData({ ...formData, contactDetails: e.target.value })
								}
								rows={3}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-service-faq">FAQ</Label>
							<Textarea
								id="edit-service-faq"
								placeholder="Frequently asked questions and preferred answers"
								value={formData.faq}
								onChange={(e) =>
									setFormData({ ...formData, faq: e.target.value })
								}
								rows={5}
								className="max-h-56 overflow-y-auto"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-service-knowledge">Knowledge Base</Label>
							<Textarea
								id="edit-service-knowledge"
								placeholder="Detailed service knowledge, edge cases, policies"
								value={formData.knowledgeBase}
								onChange={(e) =>
									setFormData({ ...formData, knowledgeBase: e.target.value })
								}
								rows={7}
								className="max-h-72 overflow-y-auto"
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsEditDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button onClick={handleEditSubmit}>Save Changes</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<TypedDeleteConfirmationDialog
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				entityType="Service"
				entityName={selectedService?.name ?? ""}
				consequences="This action cannot be undone and may affect associated agents."
				onConfirm={handleDeleteConfirm}
			/>

			<Card>
				<CardHeader>
					<CardTitle>All Services</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="mb-4">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
							<Input
								placeholder="Search by service name, company, description, or knowledge..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10"
							/>
						</div>
					</div>

					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Company</TableHead>
								<TableHead>Service Name</TableHead>
								<TableHead>Description</TableHead>
								<TableHead>Website</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredServices.map((service) => (
								<TableRow
									key={service.id}
									className="cursor-pointer hover:bg-slate-50"
									onClick={() => onNavigateToService(service.id)}
								>
									<TableCell>{service.companyName}</TableCell>
									<TableCell>{service.name}</TableCell>
									<TableCell className="max-w-md whitespace-normal break-words">
										{service.description}
									</TableCell>
									<TableCell>{service.website || "—"}</TableCell>
									<TableCell className="text-right">
										<div className="flex justify-end gap-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={(e: React.MouseEvent) => {
													e.stopPropagation();
													handleEditClick(service);
												}}
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												onClick={(e: React.MouseEvent) => {
													e.stopPropagation();
													handleDeleteClick(service);
												}}
											>
												<Trash2 className="h-4 w-4 text-red-600" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					{filteredServices.length === 0 && (
						<div className="text-center py-8 text-slate-500">
							No services found
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
